import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  Grid,
  Switch,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useHistory } from 'react-router-dom';
import { ThemeContext } from '../ThemeContext/ThemeContext';

export default function PermissionsAndGroupsPage(props) {
  const { theme } = useContext(ThemeContext);
  const history = useHistory();
  const {
    currentUser,
    serviceGroups,
    profiles,
    permissions = [],
    getServiceGroups,
    getProfiles,
    getAvailablePermissions,
    createServiceGroup,
    updateServiceGroup,
    deleteServiceGroup,
    createProfile,
    updateProfileEntity,
    deleteProfile,
  } = props;

  const PERMISSION_MANAGE_PROFILES = '#gerenciar-perfis';
  const canManage =
    currentUser?.is_super_admin ||
    (Array.isArray(permissions) && permissions.includes(PERMISSION_MANAGE_PROFILES));

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState('create');
  const [groupForm, setGroupForm] = useState({ code: '', description: '', status: 1, company_id: null, client_display_name: '', show_in_client: false, chat_limit: 5 });
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, description: '' });

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalMode, setProfileModalMode] = useState('create');
  const [profileForm, setProfileForm] = useState({ name: '' });
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [deleteProfileConfirm, setDeleteProfileConfirm] = useState({ open: false, id: null, name: '' });

  useEffect(() => {
    if (canManage) {
      getServiceGroups();
      getProfiles(true);
      getAvailablePermissions();
    }
  }, [canManage, getServiceGroups, getProfiles, getAvailablePermissions]);

  const handleOpenGroupModal = (mode, group = null) => {
    setGroupModalMode(mode);
    if (group) {
      setEditingGroupId(group.id);
      setGroupForm({
        code: String(group.code ?? ''),
        description: group.description ?? '',
        status: group.status ?? 1,
        company_id: group.company_id ?? null,
        client_display_name: group.client_display_name ?? '',
        show_in_client: !!group.show_in_client,
        chat_limit: group.chat_limit ?? 5,
      });
    } else {
      setEditingGroupId(null);
      setGroupForm({ code: '', description: '', status: 1, company_id: null, client_display_name: '', show_in_client: false, chat_limit: 5 });
    }
    setGroupModalOpen(true);
  };

  const handleCloseGroupModal = () => {
    setGroupModalOpen(false);
    setEditingGroupId(null);
    setGroupForm({ code: '', description: '', status: 1, company_id: null, client_display_name: '', show_in_client: false, chat_limit: 5 });
  };

  const handleSaveGroup = () => {
    const payload = {
      code: groupForm.code,
      description: groupForm.description,
      status: Number(groupForm.status),
      // A empresa do grupo saiu do formulário: nunca foi uma escolha de
      // verdade. Em toda empresa, TODO grupo aponta para a própria operadora
      // -- a SysFar tem 239 empresas cadastradas e os 7 grupos dela apontam
      // todos para a mesma. Pedir esse número era uma pergunta com uma
      // resposta certa só, e que devolvia 422 quando alguém errava o palpite.
      //
      // Ao editar, mantém o que já está gravado; ao criar, manda null e a API
      // resolve (ServiceGroupService::empresaDoTenant).
      company_id: groupModalMode === 'edit' ? (groupForm.company_id || null) : null,
      client_display_name: groupForm.client_display_name || null,
      show_in_client: !!groupForm.show_in_client,
      chat_limit: Number(groupForm.chat_limit) || 5,
    };
    if (groupModalMode === 'edit' && editingGroupId) {
      updateServiceGroup({ ...payload, id: editingGroupId });
    } else {
      createServiceGroup(payload);
    }
    handleCloseGroupModal();
  };

  const handleDeleteGroup = (id, description) => {
    setDeleteConfirm({ open: true, id, description });
  };

  const handleConfirmDeleteGroup = () => {
    if (deleteConfirm.id) {
      deleteServiceGroup(deleteConfirm.id);
      setDeleteConfirm({ open: false, id: null, description: '' });
    }
  };

  const handleOpenProfilePermissions = (profileId) => {
    history.push(`/configuracoes/perfil/${profileId}`);
  };

  const handleOpenProfileModal = (mode, profile = null) => {
    setProfileModalMode(mode);
    if (profile) {
      setEditingProfileId(profile.id);
      setProfileForm({ name: profile.name ?? '' });
    } else {
      setEditingProfileId(null);
      setProfileForm({ name: '' });
    }
    setProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setProfileModalOpen(false);
    setEditingProfileId(null);
    setProfileForm({ name: '' });
  };

  const handleSaveProfile = () => {
    if (profileModalMode === 'edit' && editingProfileId) {
      updateProfileEntity({ id: editingProfileId, name: profileForm.name.trim() });
    } else {
      createProfile({ name: profileForm.name.trim() });
    }
    handleCloseProfileModal();
  };

  const handleDeleteProfileClick = (id, name) => {
    setDeleteProfileConfirm({ open: true, id, name });
  };

  const handleConfirmDeleteProfile = () => {
    if (deleteProfileConfirm.id) {
      deleteProfile(deleteProfileConfirm.id);
      setDeleteProfileConfirm({ open: false, id: null, name: '' });
    }
  };

  const isProfileProtected = (profileId) => profileId === 1;

  if (!canManage) {
    return (
      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Alert severity="info">
            Apenas o super administrador (configurado no .env) ou perfis com a permissão &quot;Gerenciar perfis&quot; podem acessar esta área.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const profilesList = Array.isArray(profiles) ? profiles : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 3 }}>
      {/* Grupos de Atendimento */}
      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Grupos de Atendimento
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenGroupModal('create')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Novo grupo
            </Button>
          </Box>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1.5 }}>
            Crie, edite ou exclua grupos de atendimento. Operadores são vinculados a um grupo.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Entra na fila</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(serviceGroups || []).map((g) => (
                  <TableRow key={g.id}>
                    <TableCell>{g.description}</TableCell>
                    <TableCell>
                      <Chip
                        label={g.status === 1 ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={g.status === 1 ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Se ligado, o departamento aparece como destino no 'Transferir para departamento' do chat.">
                        <Switch
                          size="small"
                          checked={Boolean(g.queue_enabled)}
                          onChange={(e) => updateServiceGroup({
                            id: g.id,
                            code: g.code,
                            description: g.description,
                            status: g.status,
                            company_id: g.company_id,
                            queue_enabled: e.target.checked,
                          })}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenGroupModal('edit', g)} aria-label="Editar">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteGroup(g.id, g.description)} aria-label="Excluir">
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Perfis e Permissões */}
      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
              Perfis e Permissões
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenProfileModal('create')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Novo perfil
            </Button>
          </Box>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Clique em um perfil para editar as permissões em uma página dedicada. Crie, edite ou exclua perfis (exceto o perfil Administrador).
          </Typography>
          <Grid container spacing={1.5}>
            {profilesList.map((profile) => {
              const permCount = (profile.permissions ?? []).length;
              const protectedProfile = isProfileProtected(profile.id);
              return (
                /*
                 * Aqui NÃO entra `item`, e é de propósito.
                 *
                 * `xs`/`sm`/`md` sem `item` é aviso do MUI, mas o container
                 * destes cartões tem `spacing={1.5}` -- e com `item` o MUI
                 * passaria a dar 12px de padding em cada um. Os cartões já
                 * têm o afastamento deles; com o `item` ele viraria o dobro
                 * num lado só. O que está escrito abaixo é exatamente o que
                 * as três props geravam, nos mesmos pontos de quebra.
                 */
                <Grid
                  key={profile.id}
                  sx={{
                    flexGrow: 0,
                    flexBasis: { xs: '100%', sm: '50%', md: '33.3333%' },
                    maxWidth: { xs: '100%', sm: '50%', md: '33.3333%' },
                  }}
                >
                  <Card
                    elevation={0}
                    onClick={() => handleOpenProfilePermissions(profile.id)}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      margin: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(99, 165, 199, 0.06)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 0.5, mb: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <SettingsIcon sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 }} />
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.9rem' }} noWrap>
                            {profile.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleOpenProfileModal('edit', profile); }}
                            aria-label="Editar perfil"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {!protectedProfile && (
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleDeleteProfileClick(profile.id, profile.name); }}
                              aria-label="Excluir perfil"
                            >
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Chip
                        label={`${permCount} permissão${permCount !== 1 ? 'ões' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 1, '& .MuiChip-label': { fontSize: '0.75rem' } }}
                      />
                      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: `${theme.palette.text.secondary} !important` }}
                        >
                          Editar permissões
                        </Typography>
                        <ChevronRightIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Modal Criar/Editar Grupo */}
      <Dialog open={groupModalOpen} onClose={handleCloseGroupModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {groupModalMode === 'edit' ? 'Editar grupo' : 'Novo grupo de atendimento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Código"
              value={groupForm.code}
              onChange={(e) => setGroupForm((f) => ({ ...f, code: e.target.value }))}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Descrição"
              value={groupForm.description}
              onChange={(e) => setGroupForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Nome no dropdown do cliente"
              value={groupForm.client_display_name}
              onChange={(e) => setGroupForm((f) => ({ ...f, client_display_name: e.target.value }))}
              fullWidth
              variant="outlined"
              helperText="Nome exibido no dropdown de departamentos do clientweb. Se vazio, usa a Descrição."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!groupForm.show_in_client}
                  onChange={(e) => setGroupForm((f) => ({ ...f, show_in_client: e.target.checked }))}
                />
              }
              label="Aparece no dropdown do cliente (clientweb)"
            />
            <TextField
              label="Limite de chats simultâneos por técnico"
              type="number"
              inputProps={{ min: 1, max: 999 }}
              value={groupForm.chat_limit}
              onChange={(e) => setGroupForm((f) => ({ ...f, chat_limit: e.target.value }))}
              fullWidth
              variant="outlined"
              helperText="Quando todos os operadores disponíveis do departamento atingem esse número, o novo chat vai para a fila (Em Espera)."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Number(groupForm.status) === 1}
                  onChange={(e) => setGroupForm((f) => ({ ...f, status: e.target.checked ? 1 : 0 }))}
                />
              }
              label="Ativo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGroupModal}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveGroup} disabled={!groupForm.description?.trim()}>
            {groupModalMode === 'edit' ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmar exclusão grupo */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null, description: '' })}>
        <DialogTitle>Excluir grupo</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja realmente excluir o grupo &quot;{deleteConfirm.description}&quot;? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null, description: '' })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteGroup}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Criar/Editar Perfil */}
      <Dialog open={profileModalOpen} onClose={handleCloseProfileModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {profileModalMode === 'edit' ? 'Editar perfil' : 'Novo perfil'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome do perfil"
              value={profileForm.name}
              onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              variant="outlined"
              placeholder="Ex.: Suporte, Comercial"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfileModal}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveProfile} disabled={!profileForm.name?.trim()}>
            {profileModalMode === 'edit' ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmar exclusão perfil */}
      <Dialog open={deleteProfileConfirm.open} onClose={() => setDeleteProfileConfirm({ open: false, id: null, name: '' })}>
        <DialogTitle>Excluir perfil</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja realmente excluir o perfil &quot;{deleteProfileConfirm.name}&quot;? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProfileConfirm({ open: false, id: null, name: '' })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteProfile}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
